import React, { useState, useEffect } from 'react';
import { Box, SimpleGrid, Text, Alert, LoadingOverlay, Group, Switch, Divider } from '@mantine/core';
import { IconAlertCircle, IconShare } from '@tabler/icons-react';
import { useGroup } from '../../contexts/GroupContext';
import { reportsApi } from '../../api/reports';
import { DynamicChart } from './DynamicChart';
import { DashboardConfig, PerformanceData } from '../../types/dashboard';
import { getDashboardConfig } from '../../config/dashboardConfigs';
import { showApiErrorNotification } from '../../utils/notifications';
import { SectionHeader } from '../common/SectionHeader';

export const DynamicDashboard: React.FC = () => {
  const { selectedGroup } = useGroup();
  const [dashboardConfig, setDashboardConfig] = useState<DashboardConfig | null>(null);
  const [performanceData, setPerformanceData] = useState<PerformanceData | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);
  const [showPartnerData, setShowPartnerData] = useState<boolean>(false);

  // Load dashboard config and performance data when selectedGroup changes
  useEffect(() => {
    // Reset state when group changes
    setDashboardConfig(null);
    setPerformanceData(null);
    setError(null);
    setShowPartnerData(false);

    if (!selectedGroup) {
      return;
    }

    const loadDashboardData = async () => {
      setIsLoading(true);
      try {
        const config = getDashboardConfig(selectedGroup);
        console.log('Loaded dashboard config for group:', selectedGroup);
        setDashboardConfig(config);

        const data = await reportsApi.getPerformanceDataByGroup(selectedGroup);
        setPerformanceData(data);
      } catch (err) {
        console.error('Error loading dashboard data:', err);
        setError(err instanceof Error ? err : new Error('Failed to load dashboard data'));
        showApiErrorNotification(err, 'Failed to load performance data');
      } finally {
        setIsLoading(false);
      }
    };

    loadDashboardData();
  }, [selectedGroup]);

  // Determine if this dashboard can be shared with other groups
  const canShare = dashboardConfig?.canViewExternal && dashboardConfig.canViewExternal.length > 0;
  const externalGroupName = canShare ? dashboardConfig.canViewExternal.join(' + ') : '';

  if (!selectedGroup) {
    return (
      <Text color="dimmed" align="center" py="xl">
        Please select a group to view the performance dashboard.
      </Text>
    );
  }

  if (error) {
    return (
      <Alert icon={<IconAlertCircle size={16} />} title="Error Loading Dashboard" color="red" mb="xl" role="alert">
        {error.message || 'Failed to load dashboard data. Please try again later.'}
      </Alert>
    );
  }

  return (
    <Box pos="relative">
      <LoadingOverlay visible={isLoading} overlayBlur={2} />

      {dashboardConfig && (
        <>
          <Box mb="md">
            <SectionHeader title={dashboardConfig.name} />

            {canShare && (
              <Group position="apart" mt="xs" mb="md">
                <Text color="dimmed" size="sm">
                  {dashboardConfig.description}
                </Text>
                <Group spacing="xs">
                  <IconShare size={16} />
                  <Switch
                    label={
                      <Text>
                        Show{' '}
                        <Text span weight={700} component="span">
                          {externalGroupName}
                        </Text>{' '}
                        Data
                      </Text>
                    }
                    checked={showPartnerData}
                    onChange={(event) => setShowPartnerData(event.currentTarget.checked)}
                    size="sm"
                  />
                </Group>
              </Group>
            )}

            {!canShare && dashboardConfig.description && (
              <Text color="dimmed" mb="xl">
                {dashboardConfig.description}
              </Text>
            )}
          </Box>

          {/* Render each section grouping */}
          {dashboardConfig.sectionGroupings.map((section, sectionIndex) => (
            <Box key={`section-${sectionIndex}`} mb={60}>
              {/* Always show section name as a header */}
              <Box mb="md">
                <Divider
                  my="lg"
                  label={
                    <Text size="lg" weight={600}>
                      {section.name}
                    </Text>
                  }
                  labelPosition="left"
                />
              </Box>

              <SimpleGrid
                cols={section.layout.columns || 2}
                spacing="lg"
                breakpoints={[
                  { maxWidth: 1400, cols: Math.min(section.layout.columns || 2, 2) },
                  { maxWidth: 1024, cols: Math.min(section.layout.columns || 2, 2) },
                  { maxWidth: 768, cols: 1 },
                ]}
                mb="xl"
              >
                {section.layout.charts.map((chartConfig) => {
                  const chartData = performanceData?.[chartConfig.dataKey] || [];
                  return <DynamicChart key={chartConfig.id} config={chartConfig} data={chartData} isLoading={isLoading} />;
                })}
              </SimpleGrid>
            </Box>
          ))}
        </>
      )}
    </Box>
  );
};
